import { useEffect, useState } from "react";

import {
    Backdrop,
    Badge,
    Box,
    Button,
    CircularProgress,
    DialogContent,
    Grid,
    Tooltip,
} from "@mui/material";
import FilterBar from "common/components/FilterBar/FilterBar";
import GenericAlert from "common/components/GenericAlert/GenericAlert";
import GenericDialog from "common/components/GenericDialog/GenericDialog";
import SelectProvinceAndLocation from "common/components/SelectProvinceAndLocation/SelectProvinceAndLocation";
import { ApiService } from "common/services/ApiService";
import { useAuth } from "context/AuthContext";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { PENDING_NOTIFICATION_MAP } from "utils/constants";
import DialogContentInputs from "./DialogContentInputs";

const ERROR_CODE_DUPLICATE = "1";

const ERROR_CODE_MAP = {
    [ERROR_CODE_DUPLICATE]:
        "Ya existe un curso con ese nombre, año de cursada y ciclo dentro de el establecimiento!",
};

const CoursesList = ({ isSuperAdmin = false, isAdmin = false, columnVisibilityModel }) => {
    const navigate = useNavigate();

    const { auth } = useAuth();
    const [courses, setCourses] = useState([]);
    const [openLoader, setOpenLoader] = useState(false);
    const [rowCount, setRowCount] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [page, setPage] = useState(1);
    const [filter, setFilter] = useState("");
    const [extraFilter, setExtraFilter] = useState({});

    const [userRequests, setUserRequests] = useState([]);

    const [openCourseDialog, setOpenCourseDialog] = useState(false);
    const [alertInfo, setAlertInfo] = useState({
        show: false,
        severity: "success",
        text: "",
    });

    const [openGenericDialog, setOpenGenericDialog] = useState(false);
    const [selectedCourseId, setSelectedCourseId] = useState(null);
    const [handleSaveFunction, setHandleSaveFunction] = useState(null);
    const [dialogTitle, setDialogTitle] = useState("");
    const [dialogContentText, setDialogContentText] = useState("");
    const [actualEstablishment, setActualEstablishment] = useState(
        auth.selectedRole?.name === "superadmin" ? {} : auth?.selectedEstablishment
    );

    const [extraFilterComponent, setExtraFilterComponent] = useState({
        establishment:
            auth.selectedRole?.name === "superadmin" ? "" : auth?.selectedEstablishment?.id,
    });

    const [courseFormData, setCourseFormData] = useState({
        name: "",
        subject: "",
        start_date: "",
        end_date: "",
        cicle: "",
        year: "",
        establishment: auth?.selectedEstablishment?.id,
        role_name: auth?.selectedRole?.name || "superadmin",
    });

    const getUserCourseRequests = async () => {
        const dataUser = await ApiService.me(auth.access);
        const res = await ApiService.getUserCourseRequests(null, null, {
            no_page: 1,
            user: dataUser.data.id,
            role_name: auth.selectedRole?.name === "superadmin" ? null : auth.selectedRole?.name,
        });

        setUserRequests(res.data);
    };

    // Creo que con esto se evita el problema de que al cambiar de rol o establecimiento no se actualicen los cursos
    // Lo que hice fue agregar en el array del useEffect el auth.selectedRole y auth.selectedEstablishment
    // y cada vez que cambie alguno de esos valores se vuelve a llamar a getCourses
    useEffect(() => {
        const intervalId = setInterval(() => {
            getCourses(filter, extraFilter, page, pageSize);
        }, 60000);
        getUserCourseRequests();
        return () => clearInterval(intervalId);
    }, [auth.selectedRole, auth.selectedEstablishment]);

    const getCourses = async (filter = "", extraFilter = {}, page = 1, pageSize = 10) => {
        setOpenLoader(true);
        const res = await ApiService.getCourses(page, pageSize, {
            ...extraFilter,
            search: filter,
            role_name: auth?.selectedRole?.name,
            establishment: isSuperAdmin
                ? actualEstablishment?.id || null
                : auth?.selectedEstablishment?.id,
        });
        setRowCount(res.data.count);
        setPageSize(pageSize);
        setPage(page);
        setFilter(filter);
        setExtraFilter(extraFilter);
        setCourses(
            res.data.results.map((course) => ({
                id: course.id,
                name: course.name,
                establishment: course.establishment.name,
                subject: course.subject,
                startDate: course.start_date,
                endDate: course.end_date,
                cicle: course.cicle,
                year: course.year,
                notifications: course.notifications,
            }))
        );
        setOpenLoader(false);
    };

    const handleJoinCourse = async (id) => {
        const dataUser = await ApiService.me(auth.access);

        const data = {
            course: id,
            user: dataUser.data.id,
            role: auth.selectedRole.name,
        };
        const res = await ApiService.joinCourse(data);

        if (res.status === 200) {
            getUserCourseRequests();
            createAlert("success", "La solicitud se creó con éxito!");
        } else {
            createAlert("error", "Ocurrió un error, intente nuevamente!");
        }
        setOpenGenericDialog(false);
    };

    const handleQuitCourse = async (ucrId) => {
        const res = await ApiService.acceptOrRejectCourse(ucrId, {
            accepted: false,
        });

        if (res.status === 200) {
            getUserCourseRequests();
            createAlert("success", "Se ha abandonado el curso con éxito!");
        } else {
            createAlert("error", "Ocurrió un error, intente nuevamente!");
        }
        setOpenGenericDialog(false);
    };

    const handleClickJoinCourseButton = (id) => {
        setDialogTitle("¿Está seguro que desea unirse a este curso?");
        setDialogContentText(
            "Se solicitará la inscripción al curso y deberá esperar que un administrador acepte su solicitud"
        );
        setSelectedCourseId(id);
        setHandleSaveFunction(() => {
            return () => handleJoinCourse(id);
        });
        setOpenGenericDialog(true);
    };

    const handleExitCourse = async (courseId, ucrId) => {
        setDialogTitle("¿Está seguro que desea abandonar este curso?");
        setDialogContentText("Perderá el acceso al curso y a todo su contenido");
        setSelectedCourseId(courseId);
        setHandleSaveFunction(() => {
            return () => handleQuitCourse(ucrId);
        });
        setOpenGenericDialog(true);
    };
    const handleShowActivitiesFromCourse = async (id) => {
        navigate(`/${auth.selectedRole?.name}/course/${id}`);
    };

    const createAlert = (severity, text, time = 2500) => {
        setAlertInfo({ show: true, severity, text });
        setTimeout(() => setAlertInfo({ show: false, severity: "", text: "" }), time);
    };

    const adminTableCoursesColumns = [
        {
            field: "name",
            headerName: "Nombre",
            width: 150,
            renderCell: (params) => {
                const exists = userRequests.find((req) => req.course.id === params.row.id);
                if (
                    ["admin", "superadmin"].includes(auth?.selectedRole?.name) ||
                    (["teacher", "student"].includes(auth?.selectedRole?.name) &&
                        exists !== undefined &&
                        !exists.pending)
                ) {
                    return (
                        <Tooltip title={params.row.name}>
                            <Badge
                                variant="dot"
                                anchorOrigin={{
                                    vertical: "top",
                                    horizontal: "left",
                                }}
                                color="secondary"
                                invisible={
                                    ["superadmin"].includes(auth?.selectedRole?.name)
                                        ? true
                                        : params.row.notifications.filter((n) =>
                                              PENDING_NOTIFICATION_MAP[
                                                  auth?.selectedRole?.name
                                              ].includes(n.category)
                                          ).length === 0
                                }
                            >
                                {params.row.name}
                            </Badge>
                        </Tooltip>
                    );
                } else {
                    return (
                        <Tooltip title={params.row.name}>
                            <p>{params.row.name}</p>
                        </Tooltip>
                    );
                }
            },
        },
        {
            field: "subject",
            headerName: "Materia",
            width: 150,
            renderCell: (params) => params.row.subject?.name,
        },
        {
            field: "startDate",
            headerName: "Fecha de inicio",
            width: 150,
            renderCell: (params) => params.row.startDate.split("-").reverse().join("/"),
        },
        {
            field: "endDate",
            headerName: "Fecha de fin",
            width: 150,
            renderCell: (params) => params.row.endDate.split("-").reverse().join("/"),
        },
        {
            field: "cicle",
            headerName: "Ciclo lectivo",
            width: 150,
        },
        {
            field: "year",
            headerName: "Año de cursada",
            width: 150,
            renderCell: (params) => `${params.row.year}°`,
        },
        {
            field: "establishment",
            headerName: "Establecimiento",
            width: 150,
            renderCell: (params) => {
                return (
                    <Tooltip title={params.row.establishment}>
                        <p style={{ overflow: "hidden" }}>{params.row.establishment}</p>
                    </Tooltip>
                );
            },
        },
        {
            field: "actions",
            headerName: "Acciones",
            // flex: 1,
            width: 350,
            hideable: true,
            renderCell: (params) => {
                const exists = userRequests.find((req) => req.course.id === params.row.id);
                if (exists !== undefined) {
                    return (
                        <Box display={"flex"} gap={1}>
                            <Button
                                variant="contained"
                                disabled={exists?.pending === true}
                                onClick={() => {
                                    handleShowActivitiesFromCourse(params.row.id);
                                }}
                                // sx={{ backgroundColor: "#db0909" }}
                            >
                                Ver curso
                            </Button>
                            <Button
                                variant="contained"
                                disabled={exists?.pending === true}
                                onClick={() => {
                                    handleExitCourse(params.row.id, exists?.id);
                                }}
                                color="error"
                            >
                                {exists?.pending === true ? "Pendiente" : "Abandonar"}
                            </Button>
                        </Box>
                    );
                } else {
                    return (
                        <Button
                            variant="contained"
                            onClick={() => {
                                handleClickJoinCourseButton(params.row.id);
                                // handleJoinCourse(params.row.id);
                            }}
                        >
                            Unirse
                        </Button>
                    );
                }
            },
        },
        {
            field: "adminActions",
            headerName: "Acciones",
            width: 350,

            hideable: true,
            renderCell: (params) => {
                return (
                    <Button
                        variant="contained"
                        onClick={() => {
                            handleShowActivitiesFromCourse(params.row.id);
                        }}
                        // sx={{ backgroundColor: "#db0909" }}
                    >
                        Ver curso
                    </Button>
                );
            },
        },
    ];

    const handleCreateCourse = async () => {
        if (
            courseFormData.name === "" ||
            courseFormData.subject === "" ||
            courseFormData.start_date === "" ||
            courseFormData.end_date === "" ||
            courseFormData.cicle === "" ||
            courseFormData.year === ""
        ) {
            createAlert("error", "Debe completar todos los campos!");
            return;
        }

        if (dayjs(courseFormData.start_date).isAfter(dayjs(courseFormData.end_date))) {
            createAlert("error", "La fecha de inicio no puede ser posterior a la fecha de fin!");
            return;
        }

        try {
            setOpenCourseDialog(false);
            setOpenLoader(true);
            await ApiService.createCourse(courseFormData);
            setOpenLoader(false);
            getCourses(filter, extraFilter, page, pageSize);
            createAlert("success", "El curso se creó con éxito!");
            getUserCourseRequests();
        } catch (error) {
            const errorMessage =
                ERROR_CODE_MAP[error.response?.data.error_code] ||
                "Ocurrió un error, intente nuevamente!";
            createAlert("error", errorMessage, 5000);
            setOpenLoader(false);
        }
    };

    const componentFilterBarSuperAdmin = (
        <div>
            <SelectProvinceAndLocation
                handleChangeProvince={() => {}}
                handleChangeLocation={() => {}}
                handleChangeEstablishment={(establishment) => {
                    setExtraFilterComponent({
                        ...extraFilterComponent,
                        establishment: establishment?.id,
                    });
                    setActualEstablishment(establishment);
                    setCourseFormData({
                        ...courseFormData,
                        establishment: establishment?.id,
                    });
                }}
                showEstablishments
                xs={12}
                gridSizes={{
                    province: { xs: 12, lg: 4, sm: 4 },
                    location: { xs: 12, lg: 4, sm: 4 },
                    establishment: { xs: 12, lg: 4, sm: 4 },
                }}
            />
        </div>
    );

    return (
        <Grid
            container
            sx={{
                flexDirection: "column",
            }}
        >
            <Grid item xs={12}>
                <h1>Cursos</h1>
            </Grid>
            {isAdmin && (
                <Grid item xs={12} mb={4}>
                    <Button variant="contained" mb={3} onClick={() => setOpenCourseDialog(true)}>
                        Crear curso
                    </Button>
                </Grid>
            )}
            <Grid item xs={12}>
                <FilterBar
                    labelSearch="Buscar curso"
                    columnVisibilityModel={columnVisibilityModel}
                    onPageChange={getCourses}
                    openLoader={openLoader}
                    rowCount={rowCount}
                    columns={adminTableCoursesColumns}
                    rows={courses}
                    pageSize={pageSize}
                    extraFilterComponent={extraFilterComponent}
                    component={isSuperAdmin ? componentFilterBarSuperAdmin : null}
                    newEntryConfigButton={
                        isSuperAdmin
                            ? {
                                  disabled: !Boolean(extraFilterComponent?.establishment),
                                  label: "Crear curso",
                                  onClick: () => {
                                      setOpenCourseDialog(true);
                                  },
                              }
                            : {}
                    }
                />
            </Grid>

            <GenericDialog
                title={`CREAR CURSO - ${actualEstablishment?.name}`}
                open={openCourseDialog}
                setOpen={setOpenCourseDialog}
                handleSave={handleCreateCourse}
            >
                <DialogContent>
                    <DialogContentInputs
                        courseFormData={courseFormData}
                        setCourseFormData={setCourseFormData}
                        actualEstablishment={actualEstablishment}
                    />
                </DialogContent>
            </GenericDialog>

            <GenericDialog
                title={dialogTitle}
                open={openGenericDialog}
                setOpen={setOpenGenericDialog}
                handleSave={() => handleSaveFunction()}
                saveButtonText="Si"
                mandatoryText={false}
            >
                <DialogContent>{dialogContentText}</DialogContent>
            </GenericDialog>
            <Grid
                sx={{
                    position: "fixed",
                    bottom: "40%",
                    left: "45%",
                    zIndex: "9999",
                }}
            >
                {alertInfo.show && (
                    <GenericAlert severity={alertInfo.severity} text={alertInfo.text} />
                )}
            </Grid>
            <Backdrop
                sx={{
                    color: "#fff",
                    zIndex: 3,
                }}
                open={openLoader}
            >
                <CircularProgress color="inherit" />
            </Backdrop>
        </Grid>
    );
};

export default CoursesList;
